import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch } from 'react-redux';

import {
  trackContentClick,
  trackContentExplore,
  trackContentImpression,
  trackNavigate
} from 'app/actions/AnalyticsGa4Actions';
import { METADATA_CATEGORY_TYPE, UI_COMPONENT_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { mapDisplayBibToAnalyticsBibEntity } from 'app/helpers/displayBib';
import displayAvailabilityShape from 'app/shapes/displayAvailability';
import { rowShape } from 'app/shapes/browse/rows';
import Rows from './Rows';

const RowsContainer = ({ displayAvailabilitByMetadataId, rows }) => {
  const dispatch = useDispatch();

  const trackBibClick = ({ displayBib, itemPosition, uiContainerPosition, uiContainerTitle, uiContainerType }) => {
    const { metadataId, title } = displayBib;
    const displayAvailability = displayAvailabilitByMetadataId[metadataId] || {};

    dispatch(
      trackContentClick({
        bib: mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability),
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
          metadata_value: title
        },
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
          ui_container_position: uiContainerPosition,
          ui_container_title: uiContainerTitle,
          ui_container_type: uiContainerType,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackBibsRowTitleClick = ({ linkText, linkUrl, uiContainerPosition, uiContainerType }) => {
    dispatch(
      trackContentExplore({
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BROWSE,
          metadata_value: linkUrl
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_HEADING,
          ui_container_position: uiContainerPosition,
          ui_container_title: linkText,
          ui_container_type: uiContainerType
        }
      })
    );
  };

  const trackLinksRowLinkClick = ({
    itemPosition,
    linkText,
    linkUrl,
    uiContainerPosition,
    uiContainerTitle,
    uiContainerType
  }) => {
    dispatch(
      trackContentExplore({
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BROWSE,
          metadata_value: linkUrl
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_position: uiContainerPosition,
          ui_container_title: uiContainerTitle,
          ui_container_type: uiContainerType,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListCreatorClick = ({
    creatorName,
    itemPosition,
    list,
    uiContainerPosition,
    uiContainerTitle,
    uiContainerType
  }) => {
    dispatch(
      trackContentExplore({
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.USER,
          metadata_value: creatorName
        },
        ui: {
          ui_component_label: creatorName,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_position: uiContainerPosition,
          ui_container_title: uiContainerTitle,
          ui_container_type: uiContainerType,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListImageClick = ({
    impressionId,
    itemPosition,
    list,
    uiContainerPosition,
    uiContainerTitle,
    uiContainerType
  }) => {
    dispatch(
      trackContentClick({
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        properties: {
          impression_id: impressionId
        },
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE,
          ui_container_position: uiContainerPosition,
          ui_container_title: uiContainerTitle,
          ui_container_type: uiContainerType,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListImpression = ({
    impressionId,
    itemPosition,
    list,
    uiContainerPosition,
    uiContainerTitle,
    uiContainerType
  }) => {
    dispatch(
      trackContentImpression({
        properties: {
          impression_id: impressionId
        },
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        ui: {
          ui_container_position: uiContainerPosition,
          ui_container_title: uiContainerTitle,
          ui_container_type: uiContainerType,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackListTitleClick = ({
    impressionId,
    itemPosition,
    list,
    uiContainerPosition,
    uiContainerTitle,
    uiContainerType
  }) => {
    dispatch(
      trackContentClick({
        list: {
          list_id: list.id,
          list_purpose: (list.listType || '').toLowerCase(),
          list_title: list.name,
          list_visibility: list.visibility
        },
        properties: {
          impression_id: impressionId
        },
        ui: {
          ui_component_label: list.name,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_position: uiContainerPosition,
          ui_container_title: uiContainerTitle,
          ui_container_type: uiContainerType,
          ui_content_position: itemPosition
        }
      })
    );
  };

  const trackNavLinkClick = ({
    itemPosition,
    linkText,
    linkUrl,
    uiContainerPosition,
    uiContainerTitle,
    uiContainerType
  }) => {
    dispatch(
      trackNavigate({
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.NAVIGATION,
          metadata_value: linkUrl
        },
        ui: {
          ui_component_label: linkText,
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_container_position: uiContainerPosition,
          ui_container_title: uiContainerTitle,
          ui_container_type: uiContainerType,
          ui_content_position: itemPosition
        }
      })
    );
  };

  return (
    <Rows
      rows={rows}
      trackLinksRowLinkClick={trackLinksRowLinkClick}
      trackBibClick={trackBibClick}
      trackBibsRowTitleClick={trackBibsRowTitleClick}
      trackListCreatorClick={trackListCreatorClick}
      trackListImageClick={trackListImageClick}
      trackListImpression={trackListImpression}
      trackListTitleClick={trackListTitleClick}
      trackNavLinkClick={trackNavLinkClick}
    />
  );
};

RowsContainer.propTypes = {
  displayAvailabilitByMetadataId: PropTypes.objectOf(displayAvailabilityShape).isRequired,
  rows: PropTypes.arrayOf(rowShape).isRequired
};

export default RowsContainer;
